import AWSAppSyncClient, { AUTH_TYPE } from 'aws-appsync';
import gql from 'graphql-tag';
import {
  INTEGRATION_SERVICE_V1_AWS_CONFIG,
  INTEGRATION_SERVICE_V2_AWS_CONFIG,
  PROPERTY_SERVICE_V3_AWS_CONFIG,
  PROPERTY_SERVICE_V4_AWS_CONFIG,
  PROPERTY_SERVICE_V5_AWS_CONFIG,
} from '../env/Configurations';
import {
  acceptFAndBDashboardOrder,
  acceptIRDDashboardOrder,
  assignTable,
  completeFAndBDashboardOrder,
  completeIRDDashboardOrder,
  createRestaurantReservation,
  createRestaurantTable,
  declineFAndBDashboardOrder,
  delayIRDDashboardOrder,
  deleteRestaurantReservation,
  deleteRestaurantTable,
  markFAndBBillAsPaid,
  updateHouseKeepingOrderState,
  updateRestaurantOrderStatus,
  updateRestaurantReservation,
  updateRestaurantTable,
  updateSpaOrderStatus,
} from '../graphql/dashboard/mutations';
import {
  getDashboardFnBOrders,
  getDashboardHouseKeepingOrders,
  getDashboardIRDOrders,
  getDashboardOccupancyBooking,
  getDashboardOccupancyCheckin,
  getDashboardOccupancyOrders,
  getDashboardSpaOrders,
  getRestaurantReservationDetails,
} from '../graphql/dashboard/queries';
import {
  onCreateFnBBillSubscription,
  onCreateFnBOrderSubscription,
  onCreateOrderSubscription,
  onCreateSpaOrderSubscription,
  onCreateHousekeepingOrderSubscription,
  onCreateRestaurantTableSubscription,
} from '../graphql/dashboard/subscription';
import { getCurrentJwtToken, getEncodedValue } from './authUtils';

const propertyServiceV3Client = new AWSAppSyncClient({
  url: PROPERTY_SERVICE_V3_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: PROPERTY_SERVICE_V3_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

const propertyServiceV4Client = new AWSAppSyncClient({
  url: PROPERTY_SERVICE_V4_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: PROPERTY_SERVICE_V4_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

const propertyServiceV5Client = new AWSAppSyncClient({
  url: PROPERTY_SERVICE_V5_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: PROPERTY_SERVICE_V5_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

// const propertyServiceV6Client = new AWSAppSyncClient({
//   url: PROPERTY_SERVICE_V6_AWS_CONFIG.aws_appsync_graphqlEndpoint,
//   region: PROPERTY_SERVICE_V6_AWS_CONFIG.region,
//   auth: {
//     type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
//     jwtToken: async () => await getCurrentJwtToken(),
//   },
//   disableOffline: true,
//   cacheOptions: {
//     addTypename: false,
//   },
// });

const integrationServiceV1Client = new AWSAppSyncClient({
  url: INTEGRATION_SERVICE_V1_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: INTEGRATION_SERVICE_V1_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

const integrationServiceV2Client = new AWSAppSyncClient({
  url: INTEGRATION_SERVICE_V2_AWS_CONFIG.aws_appsync_graphqlEndpoint,
  region: INTEGRATION_SERVICE_V2_AWS_CONFIG.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => await getCurrentJwtToken(),
  },
  disableOffline: true,
  cacheOptions: {
    addTypename: false,
  },
});

export const acceptIRDDashboardOrders = async (hotelId, id, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(acceptIRDDashboardOrder),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      lang,
    }),
  });
};

export const getOrderList = async (hotelId, lang) => {
  return await propertyServiceV3Client.query({
    query: gql(getDashboardIRDOrders),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang: lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const completeIRDDashboardOrders = async (id, hotelId, completedTime, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(completeIRDDashboardOrder),
    variables: getEncodedValue({
      id: id,
      hotelId: hotelId,
      completedTime: completedTime,
      lang,
    }),
  });
};

export const delayIRDDashboardOrders = async (id, hotelId, delayReason, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(delayIRDDashboardOrder),
    variables: getEncodedValue({
      id: id,
      hotelId: hotelId,
      delayReason: delayReason,
      lang,
    }),
  });
};

export const irdDashboardOrderSubscription = async (hotelId) => {
  return await propertyServiceV3Client.subscribe({
    query: gql(onCreateOrderSubscription),
    variables: getEncodedValue({
      hotelId: hotelId,
    }),
  });
};

export const fnbDashboardOrderSubscription = async (hotelId, restaurantId) => {
  return await propertyServiceV5Client.subscribe({
    query: gql(onCreateFnBOrderSubscription),
    variables: getEncodedValue({
      hotelId: hotelId,
      restaurantId: restaurantId,
    }),
  });
};

export const fnbDashboardBillSubscription = async (hotelId, restaurantId) => {
  return await propertyServiceV5Client.subscribe({
    query: gql(onCreateFnBBillSubscription),
    variables: getEncodedValue({
      hotelId: hotelId,
      restaurantId: restaurantId,
    }),
  });
};

export const restaurantTableDashboardSubscription = async (hotelId, restaurantId) => {
  return await propertyServiceV3Client.subscribe({
    query: gql(onCreateRestaurantTableSubscription),
    variables: getEncodedValue({
      hotelId: hotelId,
      restaurantId: restaurantId,
    }),
  });
};

export const housekeepingOrderSubscription = async () => {
  return await propertyServiceV4Client.subscribe({
    query: gql(onCreateHousekeepingOrderSubscription),
  });
};

export const getSpaOrderList = async (hotelId, lang) => {
  return await propertyServiceV3Client.query({
    query: gql(getDashboardSpaOrders),
    variables: getEncodedValue({
      hotelId: hotelId,
      lang: lang,
    }),
    fetchPolicy: 'network-only',
  });
};

export const updateSpaDashboardOrderStatus = async (hotelId, id, status, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(updateSpaOrderStatus),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      status: status,
      lang,
    }),
  });
};

export const spaDashboardOrderSubscription = async (hotelId, spaId) => {
  return await propertyServiceV3Client.subscribe({
    query: gql(onCreateSpaOrderSubscription),
    variables: getEncodedValue({
      hotelId: hotelId,
      spaId: spaId,
    }),
  });
};

export const getRestaurantDashboardReservationDetails = async (restaurantId, lang) => {
  return await propertyServiceV3Client.query({
    query: gql(getRestaurantReservationDetails),
    variables: getEncodedValue({ restaurantId, lang }),
    fetchPolicy: 'network-only',
  });
};

export const getHousekeepingOrderList = async (hotelId, lang) => {
  return await propertyServiceV4Client.query({
    query: gql(getDashboardHouseKeepingOrders),
    variables: getEncodedValue({ hotelId, lang }),
    fetchPolicy: 'network-only',
  });
};

export const getOccupancyOrderList = async (hotelId) => {
  return await integrationServiceV1Client.query({
    query: gql(getDashboardOccupancyOrders),
    variables: getEncodedValue({ hotelId }),
    fetchPolicy: 'network-only',
  });
};

export const getOccupancyCheckins = async (hotelId) => {
  return await integrationServiceV2Client.query({
    query: gql(getDashboardOccupancyCheckin),
    variables: getEncodedValue({ hotelId }),
    fetchPolicy: 'network-only',
  });
};

export const getOccupancyBookings = async (hotelId, date) => {
  return await integrationServiceV2Client.query({
    query: gql(getDashboardOccupancyBooking),
    variables: getEncodedValue({ hotelId, date }),
    fetchPolicy: 'network-only',
  });
};

export const updateRestaurantDashboardOrderStatus = async (restaurantId, id, status, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(updateRestaurantOrderStatus),
    variables: getEncodedValue({
      restaurantId: restaurantId,
      id: id,
      status: status,
      lang,
    }),
  });
};

export const assignRestaurantTable = async (payload, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(assignTable),
    variables: getEncodedValue({
      id: payload.id,
      restaurantId: payload.restaurantId,
      tableNumbers: payload.tableNumbers,
      lang,
    }),
  });
};

export const createRestaurantReservations = async (payload, hotelId, restaurantId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(createRestaurantReservation),
    variables: getEncodedValue({
      date: payload.date,
      exposure: payload.exposure,
      isReservedForGuest: payload.isReservedForGuest,
      reserveFrom: payload.reserveFrom,
      reserveUntil: payload.reserveUntil,
      restaurantId: restaurantId,
      description: payload.description,
      firstName: payload.firstName,
      lastName: payload.lastName,
      guestType: payload.guestType,
      noOfGuests: payload.noOfGuests,
      roomNo: payload.roomNo,
      tableNumbers: payload.tableNumbers,
      hotelId: hotelId,
      lang,
    }),
  });
};

export const updateRestaurantReservations = async (payload, hotelId, restaurantId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(updateRestaurantReservation),
    variables: getEncodedValue({
      id: payload.id,
      date: payload.date,
      exposure: payload.exposure,
      isReservedForGuest: payload.isReservedForGuest,
      reserveFrom: payload.reserveFrom,
      reserveUntil: payload.reserveUntil,
      restaurantId: restaurantId,
      description: payload.description,
      firstName: payload.firstName,
      lastName: payload.lastName,
      guestType: payload.guestType,
      noOfGuests: payload.noOfGuests,
      roomNo: payload.roomNo,
      tableNumbers: payload.tableNumbers,
      hotelId: hotelId,
      lang,
    }),
  });
};

export const createRestaurantTables = async (payload, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(createRestaurantTable),
    variables: getEncodedValue({
      tableNumber: payload.tableNumber,
      capacity: payload.capacity,
      exposure: payload.exposure,
      duration: payload.duration,
      hotelId: payload.hotelId,
      restaurantId: payload.restaurantId,
      lang,
    }),
  });
};

export const updateRestaurantTables = async (payload, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(updateRestaurantTable),
    variables: getEncodedValue({
      tableNumber: payload.tableNumber,
      capacity: payload.capacity,
      exposure: payload.exposure,
      duration: payload.duration,
      hotelId: payload.hotelId,
      restaurantId: payload.restaurantId,
      lang,
    }),
  });
};

export const deleteRestaurantTables = async (tableNumber, restaurantId, hotelId, lang) => {
  return await propertyServiceV3Client.mutate({
    mutation: gql(deleteRestaurantTable),
    variables: getEncodedValue({
      tableNumber: tableNumber,
      restaurantId: restaurantId,
      hotelId: hotelId,
      lang,
    }),
  });
};

export const deleteRestaurantReservations = async (id, restaurantId, hotelId, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(deleteRestaurantReservation),
    variables: getEncodedValue({
      id: id,
      restaurantId: restaurantId,
      hotelId: hotelId,
      lang,
    }),
  });
};

export const updateHousekeepingOrderStatus = async (hotelId, id, status, lang) => {
  return await propertyServiceV4Client.mutate({
    mutation: gql(updateHouseKeepingOrderState),
    variables: getEncodedValue({
      hotelId: hotelId,
      id: id,
      status: status,
      lang,
    }),
  });
};

export const getDashboardFAndBOrderDetails = async (hotelId, restaurantId, lang) => {
  return await propertyServiceV5Client.query({
    query: gql(getDashboardFnBOrders),
    variables: getEncodedValue({ hotelId, restaurantId, lang }),
    fetchPolicy: 'network-only',
  });
};

export const acceptFAndBDashboardOrders = async (hotelId, orderId, lang, restaurantId) => {
  return await propertyServiceV5Client.mutate({
    mutation: gql(acceptFAndBDashboardOrder),
    variables: getEncodedValue({
      hotelId: hotelId,
      orderId: orderId,
      restaurantId: restaurantId,
      lang,
    }),
  });
};

export const declineFAndBDashboardOrders = async (hotelId, orderId, lang, restaurantId) => {
  return await propertyServiceV5Client.mutate({
    mutation: gql(declineFAndBDashboardOrder),
    variables: getEncodedValue({
      hotelId: hotelId,
      orderId: orderId,
      lang,
      restaurantId: restaurantId,
    }),
  });
};
export const completeFAndBDashboardOrders = async (
  hotelId,
  orderId,
  lang,
  restaurantId,
  completedTime,
) => {
  return await propertyServiceV5Client.mutate({
    mutation: gql(completeFAndBDashboardOrder),
    variables: getEncodedValue({
      hotelId: hotelId,
      orderId: orderId,
      lang,
      restaurantId: restaurantId,
      completedTime: completedTime,
    }),
  });
};

export const payFnBDashboardBIll = async (id, hotelId, restaurantId) => {
  return await propertyServiceV5Client.mutate({
    mutation: gql(markFAndBBillAsPaid),
    variables: getEncodedValue({
      billId: id,
      hotelId: hotelId,
      restaurantId: restaurantId,
    }),
  });
};
